import React, { useMemo, useState } from "react";
import { KeyRound, Plus } from "lucide-react";
import type { Permission } from "../../../features/rbac/types";
import { ConfirmDialog } from "../shared/ConfirmDialog";
import { EmptyState } from "../shared/EmptyState";
import { FormModal, LabeledField, SelectInput, TextInput } from "../shared/FormModal";
import { RowActions } from "../shared/RowActions";
import { SearchInput } from "../shared/SearchInput";
import { SectionCard } from "../shared/SectionCard";
import { StatusPill } from "../shared/StatusPill";
import { useRbacAction } from "../hooks/useRbacAction";

const DEFAULT_ACTIONS = ["read", "create", "update", "delete"];
const DEFAULT_SCOPES = ["any", "own"];

interface PermissionsTabProps {
  permissions: Permission[];
  onCreate: (payload: { action: string; scope: string }) => void;
  onUpdate: (payload: { permissionId: number; action: string; scope: string }) => void;
  onDelete: (permissionId: number) => void;
}

interface PermissionFormState {
  action: string;
  scope: string;
}

const emptyForm: PermissionFormState = { action: "read", scope: "any" };

export const PermissionsTab: React.FC<PermissionsTabProps> = ({
  permissions,
  onCreate,
  onUpdate,
  onDelete,
}) => {
  const { matches, isBusy } = useRbacAction();
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Permission | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Permission | null>(null);
  const [form, setForm] = useState<PermissionFormState>(emptyForm);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return permissions;
    return permissions.filter(
      (p) =>
        p.action.toLowerCase().includes(q) ||
        p.scope.toLowerCase().includes(q) ||
        String(p.permissionId).includes(q)
    );
  }, [permissions, search]);

  const openCreate = () => {
    setForm(emptyForm);
    setCreateOpen(true);
  };

  const openEdit = (permission: Permission) => {
    setForm({ action: permission.action, scope: permission.scope });
    setEditTarget(permission);
  };

  const closeForm = () => {
    setCreateOpen(false);
    setEditTarget(null);
    setForm(emptyForm);
  };

  const submitDisabled = !form.action.trim() || !form.scope.trim();

  const handleSubmit = () => {
    if (submitDisabled) return;
    const payload = { action: form.action.trim(), scope: form.scope.trim() };
    if (editTarget) {
      onUpdate({ permissionId: editTarget.permissionId, ...payload });
    } else {
      onCreate(payload);
    }
    closeForm();
  };

  return (
    <SectionCard
      title="Permissions"
      description="Action + scope pairs that can be granted to role-resources."
      actions={
        <>
          <SearchInput value={search} onChange={setSearch} placeholder="Search permissions" />
          <button
            type="button"
            onClick={openCreate}
            disabled={isBusy}
            className="inline-flex items-center gap-2 rounded-md bg-brand px-3 py-2 text-sm font-medium text-white hover:bg-brand/90 disabled:opacity-50"
          >
            <Plus className="h-4 w-4" />
            New permission
          </button>
        </>
      }
    >
      {filtered.length === 0 ? (
        <EmptyState
          icon={<KeyRound className="h-5 w-5" />}
          title={search ? "No permissions match your search" : "No permissions defined"}
          description={
            search
              ? "Try a different search term."
              : "Permissions are the atomic units used during access checks."
          }
          action={
            !search && (
              <button
                type="button"
                onClick={openCreate}
                className="inline-flex items-center gap-2 rounded-md bg-brand px-3 py-2 text-sm font-medium text-white hover:bg-brand/90"
              >
                <Plus className="h-4 w-4" />
                New permission
              </button>
            )
          }
        />
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-cardborder bg-gray-50 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                <th className="px-4 py-2">ID</th>
                <th className="px-4 py-2">Action</th>
                <th className="px-4 py-2">Scope</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((permission) => (
                <tr
                  key={permission.permissionId}
                  className="border-b border-gray-100 last:border-0 hover:bg-gray-50"
                >
                  <td className="px-4 py-3 font-mono text-xs text-gray-500">
                    {permission.permissionId}
                  </td>
                  <td className="px-4 py-3 font-medium text-gray-900">
                    <StatusPill variant="info">{permission.action}</StatusPill>
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    <StatusPill variant="neutral">{permission.scope}</StatusPill>
                  </td>
                  <td className="px-4 py-3">
                    <RowActions
                      onEdit={() => openEdit(permission)}
                      onDelete={() => setDeleteTarget(permission)}
                      editing={matches("updatePermission", permission.permissionId)}
                      deleting={matches("deletePermission", permission.permissionId)}
                      disabled={isBusy}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <FormModal
        open={createOpen || editTarget != null}
        title={
          editTarget
            ? `Edit permission · ${editTarget.action}:${editTarget.scope}`
            : "Create new permission"
        }
        onClose={closeForm}
        onSubmit={handleSubmit}
        submitting={
          editTarget
            ? matches("updatePermission", editTarget.permissionId)
            : matches("createPermission")
        }
        submitDisabled={submitDisabled}
        submitLabel={editTarget ? "Save changes" : "Create permission"}
      >
        <LabeledField
          label="Action"
          required
          hint="Custom values are allowed."
        >
          <div className="flex flex-col gap-2">
            <SelectInput
              value={DEFAULT_ACTIONS.includes(form.action) ? form.action : "__custom__"}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  action: e.target.value === "__custom__" ? "" : e.target.value,
                }))
              }
            >
              {DEFAULT_ACTIONS.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
              <option value="__custom__">Custom…</option>
            </SelectInput>
            {!DEFAULT_ACTIONS.includes(form.action) && (
              <TextInput
                value={form.action}
                onChange={(e) => setForm((f) => ({ ...f, action: e.target.value }))}
                placeholder="custom-action"
              />
            )}
          </div>
        </LabeledField>

        <LabeledField label="Scope" required>
          <div className="flex flex-col gap-2">
            <SelectInput
              value={DEFAULT_SCOPES.includes(form.scope) ? form.scope : "__custom__"}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  scope: e.target.value === "__custom__" ? "" : e.target.value,
                }))
              }
            >
              {DEFAULT_SCOPES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
              <option value="__custom__">Custom…</option>
            </SelectInput>
            {!DEFAULT_SCOPES.includes(form.scope) && (
              <TextInput
                value={form.scope}
                onChange={(e) => setForm((f) => ({ ...f, scope: e.target.value }))}
                placeholder="custom-scope"
              />
            )}
          </div>
        </LabeledField>
      </FormModal>

      <ConfirmDialog
        open={deleteTarget != null}
        title={`Delete permission "${deleteTarget?.action}:${deleteTarget?.scope}"?`}
        description="Existing role-resource mappings that reference this permission may break."
        confirmLabel="Delete permission"
        destructive
        loading={
          deleteTarget ? matches("deletePermission", deleteTarget.permissionId) : false
        }
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => {
          if (deleteTarget) {
            onDelete(deleteTarget.permissionId);
            setDeleteTarget(null);
          }
        }}
      />
    </SectionCard>
  );
};
